import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { PurchaseOrder, PurchaseOrderFilters, PURCHASE_ORDER_STATUS_LABELS } from '../../types/purchaseOrder';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { generatePurchaseOrderPDF } from '../../utils/purchaseOrderPdfGenerator';
import { companyService } from '../../services/companyService';
import { invoiceTemplateService } from '../../services/invoiceTemplateService';

interface PurchaseOrderListProps {
  filters: PurchaseOrderFilters;
  onViewPO: (po: PurchaseOrder) => void;
  onEditPO: (po: PurchaseOrder) => void;
  expandedPOId: string | null;
  setExpandedPOId: (id: string | null) => void;
}

const poStatusStyle: Record<string, React.CSSProperties> = {
  DRAFT:     { background: 'rgba(120,120,140,0.15)', color: '#9a9ab0', border: '1px solid rgba(120,120,140,0.25)' },
  ORDERED:   { background: 'rgba(90,140,201,0.12)', color: '#5a8cc9', border: '1px solid rgba(90,140,201,0.25)' },
  PARTIALLY_RECEIVED: { background: 'rgba(92,78,51,0.22)', color: '#d97706', border: '1px solid rgba(92,78,51,0.35)' },
  RECEIVED:  { background: 'rgba(90,201,140,0.12)', color: '#5ac98c', border: '1px solid rgba(90,201,140,0.25)' },
  CANCELLED: { background: 'rgba(80,80,80,0.15)', color: '#555570', border: '1px solid rgba(80,80,80,0.2)' },
};

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  borderBottom: '1px solid rgba(241,245,249,1)',
  verticalAlign: 'middle',
};

const subThStyle: React.CSSProperties = {
  padding: '8px 12px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 8,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  whiteSpace: 'nowrap',
};

const subTdStyle: React.CSSProperties = {
  padding: '8px 12px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 11,
  color: '#5b5a52',
  borderBottom: '1px solid rgba(241,245,249,1)',
  verticalAlign: 'middle',
};

export const PurchaseOrderList: React.FC<PurchaseOrderListProps> = ({
  filters,
  onViewPO,
  onEditPO,
  expandedPOId,
  setExpandedPOId: _setExpandedPOId
}) => {
  const queryClient = useQueryClient();
  const [markingAsOrdered, setMarkingAsOrdered] = useState<string | null>(null);
  const [cancellingPO, setCancellingPO] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['purchase-orders', filters],
    queryFn: () => purchaseOrderService.getPurchaseOrders(filters)
  });

  const { data: company } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => companyService.getProfile()
  });

  const { data: templates } = useQuery({
    queryKey: ['invoice-templates'],
    queryFn: () => invoiceTemplateService.getAll()
  });

  const defaultTemplate = templates?.find((t) => t.isDefault) || templates?.[0];

  const markAsOrderedMutation = useMutation({
    mutationFn: purchaseOrderService.markPurchaseOrderAsOrdered,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      setMarkingAsOrdered(null);
    },
    onError: (error: any) => {
      console.error('Error marking PO as ordered:', error);
      alert(error.response?.data?.message || 'Failed to mark purchase order as ordered');
      setMarkingAsOrdered(null);
    }
  });

  const cancelPOMutation = useMutation({
    mutationFn: purchaseOrderService.cancelPurchaseOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      setCancellingPO(null);
    },
    onError: (error: any) => {
      console.error('Error cancelling PO:', error);
      alert(error.response?.data?.message || 'Failed to cancel purchase order');
      setCancellingPO(null);
    }
  });

  const handleExportPDF = (po: PurchaseOrder) => {
    const addressParts = [
      company?.address,
      company?.city,
      company?.state,
      company?.country
    ].filter(Boolean);

    generatePurchaseOrderPDF({
      purchaseOrder: po,
      companyName: company?.name || '',
      companyAddress: addressParts.join(', '),
      companyPhone: company?.phone || '',
      companyEmail: company?.email || '',
      template: po.template || defaultTemplate
    });
  };

  const handleMarkAsOrdered = (po: PurchaseOrder, event: React.MouseEvent) => {
    event.stopPropagation();
    if (window.confirm(`Mark purchase order ${po.poNumber} as ORDERED? This action will allow the PO to be received.`)) {
      setMarkingAsOrdered(po.id);
      markAsOrderedMutation.mutate(po.id);
    }
  };

  const handleCancelPO = (po: PurchaseOrder, event: React.MouseEvent) => {
    event.stopPropagation();
    if (window.confirm(`Cancel purchase order ${po.poNumber}? This action cannot be undone.`)) {
      setCancellingPO(po.id);
      cancelPOMutation.mutate(po.id);
    }
  };

  if (isLoading) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033' }}>Purchase Orders</div>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{ height: 36, background: 'rgba(241,245,249,1)', borderRadius: 3 }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    console.error('Purchase orders loading error:', error);
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(201,90,90,0.2)', borderRadius: 6, padding: 24, textAlign: 'center' }}>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#c95a5a' }}>Error loading purchase orders. Please try again.</p>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 6 }}>{(error as Error).message || 'Unknown error'}</p>
      </div>
    );
  }

  if (!data?.purchaseOrders.length) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 48, textAlign: 'center' }}>
        <svg width="40" height="40" fill="none" stroke="rgba(217,119,6,0.25)" viewBox="0 0 24 24" style={{ margin: '0 auto 12px' }}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, letterSpacing: '0.2em', color: '#6b6a5c' }}>NO PURCHASE ORDERS FOUND</p>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#8a8778', marginTop: 6 }}>Get started by creating your first purchase order.</p>
      </div>
    );
  }

  return (
    <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6 }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033' }}>Purchase Orders</div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>
            {data.pagination?.totalItems ?? 0} total purchase orders
          </div>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'rgba(201,165,90,0.02)' }}>
              <th style={thStyle}>PO Number</th>
              <th style={thStyle}>Vendor</th>
              <th style={thStyle}>Order Date</th>
              <th style={thStyle}>Expected Delivery</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Total</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.purchaseOrders.map((po: PurchaseOrder) => (
              <React.Fragment key={po.id}>
                <tr
                  style={{
                    transition: 'background 0.15s',
                    cursor: 'pointer',
                    background: expandedPOId === po.id ? 'rgba(239,232,213,0.5)' : 'transparent',
                  }}
                  onMouseEnter={e => { if (expandedPOId !== po.id) e.currentTarget.style.background = 'rgba(248,250,252,1)'; }}
                  onMouseLeave={e => { if (expandedPOId !== po.id) e.currentTarget.style.background = 'transparent'; }}
                  onClick={() => onViewPO(po)}
                >
                  <td style={tdStyle}>
                    <div style={{ color: '#d97706', fontWeight: 500 }}>{po.poNumber}</div>
                  </td>
                  <td style={tdStyle}>
                    <div>{po.vendor?.name || 'N/A'}</div>
                  </td>
                  <td style={tdStyle}>
                    <div>{formatDate(po.orderDate)}</div>
                  </td>
                  <td style={tdStyle}>
                    <div>{po.expectedDeliveryDate ? formatDate(po.expectedDeliveryDate) : <span style={{ color: '#8a8778' }}>—</span>}</div>
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      ...(poStatusStyle[po.status] || poStatusStyle.DRAFT),
                      display: 'inline-block',
                      padding: '3px 10px',
                      borderRadius: 3,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 8,
                      letterSpacing: '0.15em',
                      textTransform: 'uppercase',
                    }}>
                      {PURCHASE_ORDER_STATUS_LABELS[po.status] || po.status}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: '#fbbf24' }}>
                      {formatCurrency(po.total)}
                    </div>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); onViewPO(po); }}
                        style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#d97706', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                      >
                        View
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); onEditPO(po); }}
                        disabled={po.status !== 'DRAFT'}
                        style={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 11,
                          color: po.status !== 'DRAFT' ? '#c9c2ab' : '#5b5a52',
                          background: 'none',
                          border: 'none',
                          cursor: po.status !== 'DRAFT' ? 'not-allowed' : 'pointer',
                          padding: 0,
                        }}
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>

                {expandedPOId === po.id && po.items && (
                  <tr>
                    <td colSpan={7} style={{ padding: 0, borderBottom: '1px solid rgba(241,245,249,1)' }}>
                      <div style={{ background: '#faf7ee', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, letterSpacing: '0.2em', color: '#d97706', textTransform: 'uppercase' }}>
                          Purchase Order Items
                        </div>

                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ background: 'rgba(201,165,90,0.02)' }}>
                              <th style={{ ...subThStyle, textAlign: 'left' }}>Product</th>
                              <th style={{ ...subThStyle, textAlign: 'left' }}>Description</th>
                              <th style={{ ...subThStyle, textAlign: 'right' }}>Ordered</th>
                              <th style={{ ...subThStyle, textAlign: 'right' }}>Received</th>
                              <th style={{ ...subThStyle, textAlign: 'right' }}>Rate</th>
                              <th style={{ ...subThStyle, textAlign: 'right' }}>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {po.items?.map((item, index) => {
                              const amount = item.quantity * item.rate;
                              const isFullyReceived = item.receivedQuantity >= item.quantity;
                              const isPartiallyReceived = item.receivedQuantity > 0 && item.receivedQuantity < item.quantity;
                              return (
                                <tr key={index}
                                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(201,165,90,0.02)')}
                                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                >
                                  <td style={{ ...subTdStyle, color: '#1c2033' }}>{item.product?.name || 'N/A'}</td>
                                  <td style={{ ...subTdStyle, color: '#6b6a5c' }}>{item.description || '—'}</td>
                                  <td style={{ ...subTdStyle, textAlign: 'right' }}>{item.quantity}</td>
                                  <td style={{ ...subTdStyle, textAlign: 'right' }}>
                                    <span style={{
                                      fontWeight: 500,
                                      color: isFullyReceived ? '#5ac98c' : isPartiallyReceived ? '#d97706' : '#5b5a52',
                                    }}>
                                      {item.receivedQuantity}
                                    </span>
                                  </td>
                                  <td style={{ ...subTdStyle, textAlign: 'right' }}>{formatCurrency(item.rate)}</td>
                                  <td style={{ ...subTdStyle, textAlign: 'right', color: '#fbbf24', fontFamily: "'Inter', sans-serif", fontSize: 14 }}>
                                    {formatCurrency(amount)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>

                        {/* Totals */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 32, paddingTop: 12, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.15em', color: '#6b6a5c', textTransform: 'uppercase' }}>Subtotal</div>
                            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52', marginTop: 4 }}>{formatCurrency(po.subtotal)}</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.15em', color: '#6b6a5c', textTransform: 'uppercase' }}>Tax</div>
                            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52', marginTop: 4 }}>{formatCurrency(po.taxAmount)}</div>
                          </div>
                          {po.shippingCost > 0 && (
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.15em', color: '#6b6a5c', textTransform: 'uppercase' }}>Shipping</div>
                              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52', marginTop: 4 }}>{formatCurrency(po.shippingCost)}</div>
                            </div>
                          )}
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.15em', color: '#6b6a5c', textTransform: 'uppercase' }}>Total</div>
                            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, color: '#fbbf24', marginTop: 4 }}>{formatCurrency(po.total)}</div>
                          </div>
                        </div>

                        {po.notes && (
                          <div style={{ paddingTop: 12, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
                            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.15em', color: '#6b6a5c', textTransform: 'uppercase', marginBottom: 6 }}>Notes</div>
                            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52' }}>{po.notes}</div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div style={{ paddingTop: 12, borderTop: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', gap: 16 }}>
                            {po.status === 'DRAFT' && (
                              <button
                                onClick={(e) => handleMarkAsOrdered(po, e)}
                                disabled={markingAsOrdered === po.id}
                                style={{
                                  fontFamily: "'Inter', sans-serif",
                                  fontSize: 11,
                                  color: markingAsOrdered === po.id ? '#3a5a3a' : '#5ac98c',
                                  background: 'none',
                                  border: 'none',
                                  cursor: markingAsOrdered === po.id ? 'not-allowed' : 'pointer',
                                  padding: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6,
                                }}
                              >
                                <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                {markingAsOrdered === po.id ? 'Marking as Ordered...' : 'Mark as Ordered'}
                              </button>
                            )}
                            {(po.status === 'DRAFT' || po.status === 'ORDERED') && (
                              <button
                                onClick={(e) => handleCancelPO(po, e)}
                                disabled={cancellingPO === po.id}
                                style={{
                                  fontFamily: "'Inter', sans-serif",
                                  fontSize: 11,
                                  color: cancellingPO === po.id ? '#5a3a3a' : '#c95a5a',
                                  background: 'none',
                                  border: 'none',
                                  cursor: cancellingPO === po.id ? 'not-allowed' : 'pointer',
                                  padding: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6,
                                }}
                              >
                                <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                {cancellingPO === po.id ? 'Cancelling...' : 'Cancel PO'}
                              </button>
                            )}
                          </div>

                          <button
                            onClick={() => handleExportPDF(po)}
                            style={{
                              fontFamily: "'Inter', sans-serif",
                              fontSize: 11,
                              color: '#d97706',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                            }}
                          >
                            <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Export to PDF
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data.pagination && data.pagination.totalPages > 1 && (
        <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>
            Showing {((data.pagination.currentPage - 1) * 10) + 1}–{Math.min(data.pagination.currentPage * 10, data.pagination.totalItems)} of {data.pagination.totalItems}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              disabled={data.pagination.currentPage === 1}
              style={{
                padding: '4px 12px',
                fontFamily: "'Inter', sans-serif",
                fontSize: 9,
                letterSpacing: '0.1em',
                background: 'rgba(239,232,213,0.5)',
                color: data.pagination.currentPage === 1 ? '#c9c2ab' : '#8a8778',
                border: '1px solid rgba(92,78,51,0.22)',
                borderRadius: 3,
                cursor: data.pagination.currentPage === 1 ? 'not-allowed' : 'pointer',
                opacity: data.pagination.currentPage === 1 ? 0.5 : 1,
              }}
            >
              Previous
            </button>
            <button
              disabled={data.pagination.currentPage === data.pagination.totalPages}
              style={{
                padding: '4px 12px',
                fontFamily: "'Inter', sans-serif",
                fontSize: 9,
                letterSpacing: '0.1em',
                background: 'rgba(239,232,213,0.5)',
                color: data.pagination.currentPage === data.pagination.totalPages ? '#c9c2ab' : '#8a8778',
                border: '1px solid rgba(92,78,51,0.22)',
                borderRadius: 3,
                cursor: data.pagination.currentPage === data.pagination.totalPages ? 'not-allowed' : 'pointer',
                opacity: data.pagination.currentPage === data.pagination.totalPages ? 0.5 : 1,
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
